import type { AdminInbox } from "@mm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { api } from "@/lib/api";
import { ago, when } from "@/lib/dates";
import { Badge, Button, Card, Empty, ErrorText, Section } from "@/ui/kit";
import { PageHeader } from "@/ui/Shell";

export default function Inbox() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["inbox"], queryFn: () => api<AdminInbox>("/v1/admin/inbox"), refetchInterval: 60_000 });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const d = q.data;

  async function act(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ["inbox"] });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }
  const total = d ? Object.values(d.counts).reduce((a, b) => a + b, 0) : 0;

  return (
    <>
      <PageHeader title="Inbox" subtitle={d ? (total ? `${total} ${total === 1 ? "thing needs" : "things need"} you` : "All caught up 💛") : " "} />
      <ErrorText error={error ?? q.error} />
      {d && total === 0 ? <Empty title="Nothing waiting">New suggestions, reports and urgent sitting requests show up here.</Empty> : null}

      {d?.unansweredCare.length ? (
        <Section title="Moms still looking for a sitter" action={<Link to="/coop" className="text-sm font-semibold underline underline-offset-2">All co-op requests →</Link>}>
          <div className="grid gap-2">
            {d.unansweredCare.map((r) => {
              const hours = (Date.parse(r.startsAt) - Date.now()) / 3600_000;
              return (
                <Card key={r.id}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{r.title}</p>
                      <p className="text-sm text-muted">{r.requester}{r.neighborhood ? ` · ${r.neighborhood}` : ""}</p>
                    </div>
                    <Badge tone={hours < 24 ? "blush" : "butter"}>{hours < 24 ? `In ${Math.max(1, Math.round(hours))}h` : when(r.startsAt)}</Badge>
                  </div>
                  <p className="mt-2 text-sm">
                    {r.offers ? `${r.offers} offer${r.offers === 1 ? "" : "s"} waiting for her decision` : "No offers yet"} · {r.notified} matching {r.notified === 1 ? "mom" : "moms"} notified
                  </p>
                </Card>
              );
            })}
          </div>
        </Section>
      ) : null}

      {d?.reports.length ? (
        <Section title="Reports">
          <div className="grid gap-2">
            {d.reports.map((r) => (
              <Card key={r.id}>
                <div className="mb-1 flex items-center gap-2">
                  <Badge tone={r.reason === "safety" ? "blush" : "mist"}>{r.reason}</Badge>
                  <span className="text-sm text-muted">{ago(r.createdAt)}{r.context ? ` · about a ${r.context}` : ""}</span>
                </div>
                <p><span className="font-semibold">{r.reporter}</span> reported <span className="font-semibold">{r.subject}</span></p>
                {r.details ? <p className="mt-1 text-sm text-ink-soft">“{r.details}”</p> : null}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button variant="secondary" onClick={() => nav(`/members/${r.subjectId}`)}>View {r.subject.split(" ")[0]}</Button>
                  <Button variant="ghost" loading={busy === `ra${r.id}`} onClick={() => act(`ra${r.id}`, () => api(`/v1/admin/reports/${r.id}`, { method: "PATCH", body: { status: "actioned" } }))}>Handled</Button>
                  <Button variant="ghost" loading={busy === `rd${r.id}`} onClick={() => act(`rd${r.id}`, () => api(`/v1/admin/reports/${r.id}`, { method: "PATCH", body: { status: "dismissed" } }))}>Dismiss</Button>
                </div>
              </Card>
            ))}
          </div>
        </Section>
      ) : null}

      {d?.eventSuggestions.length ? (
        <Section title="Event ideas from moms">
          <div className="grid gap-2">
            {d.eventSuggestions.map((s) => (
              <Card key={s.id}>
                <p className="font-semibold">{s.title}</p>
                <p className="text-sm text-muted">{s.whenText} · {s.locationText}</p>
                <p className="mt-1 text-sm text-ink-soft">{s.details}</p>
                <p className="mt-1 text-xs text-muted">From {s.suggestedBy} · {ago(s.createdAt)}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    loading={busy === `ea${s.id}`}
                    onClick={() => act(`ea${s.id}`, async () => {
                      const r = await api<{ eventId: string }>(`/v1/admin/event-suggestions/${s.id}/approve`, { method: "POST" });
                      nav(`/events/${r.eventId}`);
                    })}
                  >
                    Make it an event
                  </Button>
                  <Button variant="ghost" loading={busy === `er${s.id}`} onClick={() => act(`er${s.id}`, () => api(`/v1/admin/event-suggestions/${s.id}/reject`, { method: "POST" }))}>Not now</Button>
                </div>
              </Card>
            ))}
          </div>
        </Section>
      ) : null}

      {d?.placeSuggestions.length ? (
        <Section title="New place suggestions">
          <div className="grid gap-2">
            {d.placeSuggestions.map((s) => (
              <Card key={s.id}>
                <p className="font-semibold">{s.name}</p>
                <p className="text-sm text-muted">{s.address}</p>
                {s.why ? <p className="mt-1 text-sm text-ink-soft">“{s.why}”</p> : null}
                <p className="mt-1 text-xs text-muted">From {s.suggestedBy} · {ago(s.createdAt)}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    loading={busy === `pa${s.id}`}
                    onClick={() => act(`pa${s.id}`, async () => {
                      const r = await api<{ placeId: string }>(`/v1/admin/place-suggestions/${s.id}/approve`, { method: "POST" });
                      nav(`/places/${r.placeId}`);
                    })}
                  >
                    Add to map
                  </Button>
                  <Button variant="ghost" loading={busy === `pr${s.id}`} onClick={() => act(`pr${s.id}`, () => api(`/v1/admin/place-suggestions/${s.id}/reject`, { method: "POST" }))}>Decline</Button>
                </div>
              </Card>
            ))}
          </div>
        </Section>
      ) : null}

      {d?.deadLetters.length ? (
        <Section title="Failed background jobs">
          <p className="mb-2 text-sm text-muted">These retried automatically and still failed (for example, an email bounced). Retry once the cause is fixed.</p>
          <div className="grid gap-2">
            {d.deadLetters.map((j) => (
              <Card key={j.id}>
                <p className="font-semibold">{j.queue}</p>
                <p className="line-clamp-2 text-sm text-muted">{j.error}</p>
                <p className="text-xs text-muted">{j.attempts} attempts · {ago(j.failedAt)}</p>
                <div className="mt-3 flex gap-2">
                  <Button variant="secondary" loading={busy === `dr${j.id}`} onClick={() => act(`dr${j.id}`, () => api(`/v1/admin/dead-letters/${j.id}/retry`, { method: "POST" }))}>Retry</Button>
                  <Button variant="ghost" loading={busy === `dd${j.id}`} onClick={() => act(`dd${j.id}`, () => api(`/v1/admin/dead-letters/${j.id}/resolve`, { method: "POST" }))}>Dismiss</Button>
                </div>
              </Card>
            ))}
          </div>
        </Section>
      ) : null}
    </>
  );
}
